import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  ChildTranslationService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  DEFAULT_CHIP_TRANSLATION_KEY,
  UPGRADE_PAGE_SLOT,
} from '../upgrade-journey-content';
import { ProductFeatureUpgradeTranslationService } from '../../services/product-feature-upgrade-translation.service';

/**
 * Full-page upgrade prompt for users who land on a route for a feature their
 * plan does not include.
 *
 * The chip, heading, and paragraph copy is centrally owned by the Control
 * Center (CC) team. The {@link ProductFeatureUpgradeTranslationService} resolves
 * the translation *key* for each field of the `upgrade-page` slot (Kameleoon
 * selects the variation per consent; the keys come from the bundled default
 * content), and the template binds those keys live via
 * `getTranslationForKey(...) | subscribe` so the copy renders as soon as the
 * i18n bundle loads. The component takes no copy inputs — copy is config-driven.
 *
 * The CTA is delegated to the embedded
 * `<tswp-product-feature-upgrade-button>`, which owns the resolve / track /
 * redirect behaviour. The page forwards its inputs down to the button; the
 * button's cancelable `comparePlans` event bubbles (composed) through the page
 * to its consumers natively, so the page does not re-emit it.
 *
 * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-product-feature-upgrade-page | Documentation}
 */
@Component({
  selector: 'tswp-product-feature-upgrade-page',
  templateUrl: './product-feature-upgrade-page.component.html',
  styleUrls: ['./product-feature-upgrade-page.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false,
})
export class ProductFeatureUpgradePageComponent implements OnInit {
  public static readonly selector = 'tswp-product-feature-upgrade-page';

  private static readonly TYPE = 'ProductFeatureUpgradePageComponent';

  /**
   * Required key of the gated product feature the page promotes. Used to look
   * up the slot copy in the content config and forwarded to the embedded
   * `<tswp-product-feature-upgrade-button>` for click tracking.
   */
  @Input({ required: true }) public productFeatureKey!: string;

  /**
   * Optional extra query parameters appended to the resolved compare-plans URL,
   * as a URL-encoded query string with or without a leading `?` (e.g.
   * `"?feature=reviews&campaign=q3"`). Forwarded to the embedded
   * `<tswp-product-feature-upgrade-button>`.
   */
  @Input() public redirectUrlParams?: string;

  /**
   * Resolved translation key for the chip, bound live in the template. `null`
   * when nothing resolved (nothing is rendered).
   */
  public chipKey: string | null = null;

  /**
   * Resolved translation key for the heading, bound live in the template.
   */
  public headingKey: string | null = null;

  /**
   * Resolved translation key for the paragraph, bound live in the template.
   */
  public paragraphKey: string | null = null;

  public constructor(
    @Inject(COMMON_TOKENS.TranslationService)
    public readonly translationService: ChildTranslationService,
    private readonly upgradeTranslationService: ProductFeatureUpgradeTranslationService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  public async ngOnInit(): Promise<void> {
    const keys = await this.upgradeTranslationService.resolveSlotKeys(
      ProductFeatureUpgradePageComponent.TYPE,
      this.productFeatureKey,
      UPGRADE_PAGE_SLOT,
      [
        { field: 'chip', defaultKey: DEFAULT_CHIP_TRANSLATION_KEY },
        { field: 'heading' },
        { field: 'paragraph' },
      ]
    );

    this.chipKey = keys['chip'];
    this.headingKey = keys['heading'];
    this.paragraphKey = keys['paragraph'];

    // The keys are resolved after an async Kameleoon read, so nudge change
    // detection to bind the live translations for this Shadow-DOM component.
    this.changeDetectorRef.markForCheck();
  }
}
